/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React from 'react';

import { Color } from '../../../shared/constants/Colors';

import { useSize } from '../../../shared/hooks/useSize';
import * as S from './Button.styles';

export const ButtonTheme = {
  DARK: 'dark',
  WOOCLAP: 'wooclap',
  WOOFLASH: 'wooflash',
};

export const ButtonAppearance = {
  PRIMARY: 'primary',
  CIRCULAR: 'circular',
  CIRCULAR_FLOATING: 'circular-floating',
  MINIMAL: 'minimal',
  OUTLINE: 'outline',
  NEGATIVE: 'negative',
};

export const Button = ({
  appearance,
  children,
  dataTrackAttribute,
  iconColor,
  iconHoverColor,
  isDisabled,
  isGatsbyLink,
  isIconOnRight,
  isTransparent,
  link,
  onClick,
  target,
  theme,
  form,
}) => {
  if (!children) {
    return null;
  }

  const screenWidth = useSize().width;

  const isMobile = screenWidth <= 768;

  var isNoFollow = false;

  if (link !== undefined && link.includes('#nofollow')) {
    isNoFollow = true;
    link = link.replaceAll('#nofollow', '');
  }

  const commonProps = {
    appearance,
    iconColor,
    iconHoverColor,
    isDisabled,
    isIconOnRight,
    isTransparent,
    target,
    theme,
    form,
  };

  if (link) {
    if (isGatsbyLink && !isMobile) {
      return (
        <S.StyledGatsbyLink
          {...commonProps}
          data-track={dataTrackAttribute}
          to={link}
        >
          {children}
        </S.StyledGatsbyLink>
      );
    }

    // use a <a> instead of a Link if the link props is an external url to prevent warnings.
    return (
      <S.StyledLink
        {...commonProps}
        data-track={dataTrackAttribute}
        rel={isNoFollow ? 'nofollow' : ''}
        href={link}
      >
        {children}
      </S.StyledLink>
    );
  }

  return (
    <S.StyledButton
      appearance={appearance}
      data-track={dataTrackAttribute}
      iconColor={iconColor}
      iconHoverColor={iconHoverColor}
      isDisabled={isDisabled}
      isIconOnRight={isIconOnRight}
      isTransparent={isTransparent}
      onClick={onClick}
      theme={theme}
    >
      {children}
    </S.StyledButton>
  );
};

Button.propTypes = {
  /**
   * Set the appareance of the button.
   */
  appearance: PropTypes.oneOf(Object.values(ButtonAppearance)),
  /**
   * The displayed content of the button.
   */
  children: PropTypes.node.isRequired,
  /**
   * Optional data track attribute.
   */
  dataTrackAttribute: PropTypes.string,
  /**
   * Allow the component to be used from its form.
   */
  form: PropTypes.string,
  /**
   * Set a color to the icon. Effective when appearance = circular.
   */
  iconColor: PropTypes.oneOf(Object.values(Color)),
  /**
   * Set a color on the icon when hovering the button.
   * Effective when appearance = circular
   * or appearance = circular-floating.
   */
  iconHoverColor: PropTypes.oneOf(Object.values(Color)),
  /**
   * Prevents users from clicking on the button.
   */
  isDisabled: PropTypes.bool,
  /**
   * Optimize usage of internal links.
   * Use a <Link> component instead of a <a> if true.
   */
  isGatsbyLink: PropTypes.bool,
  /**
   * Displays the Icon on the right of the button label
   * when the appearance is set to Primary.
   */
  isIconOnRight: PropTypes.bool,
  /**
   * Set transparent the circular icon.
   */
  isTransparent: PropTypes.bool,
  /**
   * Url that make button behaves like a link.
   */
  link: PropTypes.string,
  /**
   * Function called every time the event is triggered
   */
  onClick: PropTypes.func,
  /**
   * Optional button target.
   */
  target: PropTypes.string,
  /**
   * Wooclap or Wooflash theme.
   */
  theme: PropTypes.oneOf(Object.values(ButtonTheme)),
};

Button.defaultProps = {
  appearance: ButtonAppearance.PRIMARY,
  isDisabled: false,
  isGatsbyLink: true,
  isIconOnRight: false,
  isTransparent: false,
  theme: ButtonTheme.WOOCLAP,
};
